import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, fromEvent, merge, Subject, timer } from 'rxjs';
import { filter, takeUntil, throttleTime } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '@/src/environments/environment';
import {
  SessionWarningAction,
  SessionWarningDialogComponent
} from '../components/session-warning-dialog-modal/session-warning-dialog.component';
import { LoggerService } from '@shared/services/logger.service';

interface SessionConfig {
  sessionTimeoutMs: number;
  warningBeforeExpiryMs: number;
  checkIntervalMs: number;
}

const DEFAULT_CONFIG: SessionConfig = {
  sessionTimeoutMs: 30 * 60 * 1000,
  warningBeforeExpiryMs: 5 * 60 * 1000,
  checkIntervalMs: 60 * 1000
};

const LAST_ACTIVITY_KEY = 'stx_last_activity';
const SESSION_EXPIRED_KEY = 'stx_session_expired';

@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {
  private readonly config: SessionConfig = DEFAULT_CONFIG;

  private httpClient = inject(HttpClient);
  private dialog = inject(MatDialog);
  private authService = inject(AuthService);
  private ngZone = inject(NgZone);
  private loggerService = inject(LoggerService);

  private destroy$ = new Subject<void>();
  private warningDialogOpen = false;
  private monitoringActive = false;
  private autoLogoutTimer: ReturnType<typeof setTimeout> | null = null;
  private activeWarningDialogRef: ReturnType<MatDialog['open']> | null = null;

  constructor() {
    this.authService.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.startSessionMonitoring();
      } else {
        this.stopSessionMonitoring();
      }
    });
  }

  private startSessionMonitoring(): void {
    if (this.monitoringActive) {
      return;
    }

    this.destroy$ = new Subject<void>();
    this.recordActivity();
    this.clearSessionExpiredFlag();
    this.monitoringActive = true;

    this.setupActivityTracking();
    this.setupSessionCheck();
  }

  private stopSessionMonitoring(): void {
    this.monitoringActive = false;
    this.clearAutoLogoutTimer();
    this.destroy$.next();
  }

  private lastActivityFallback = 0;

  private get lastActivityTime(): number {
    try {
      return parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
    } catch {
      return this.lastActivityFallback;
    }
  }

  private recordActivity(): void {
    const now = Date.now();
    this.lastActivityFallback = now;
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    } catch (e) {
      this.loggerService.error('localStorage unavailable:', e);
    }
  }

  private setupActivityTracking(): void {
    this.ngZone.runOutsideAngular(() => {
      merge(fromEvent(document, 'click'), fromEvent(document, 'keydown'), fromEvent(document, 'mousemove'))
        .pipe(
          takeUntil(this.destroy$),
          filter(() => !this.warningDialogOpen),
          throttleTime(30000)
        )
        .subscribe(() => this.recordActivity());

      fromEvent(document, 'visibilitychange')
        .pipe(
          takeUntil(this.destroy$),
          filter(() => document.visibilityState === 'visible'),
          filter(() => !this.warningDialogOpen)
        )
        .subscribe(() => this.checkSessionState());

      fromEvent(document, 'visibilitychange')
        .pipe(
          takeUntil(this.destroy$),
          filter(() => document.visibilityState === 'visible'),
          filter(() => this.warningDialogOpen)
        )
        .subscribe(() => this.forceLogoutIfSessionExpired());

      fromEvent<StorageEvent>(window, 'storage')
        .pipe(
          takeUntil(this.destroy$),
          filter(event => event.key === LAST_ACTIVITY_KEY && this.warningDialogOpen)
        )
        .subscribe(() => this.dismissWarningIfSessionValid());

      fromEvent<StorageEvent>(window, 'storage')
        .pipe(
          takeUntil(this.destroy$),
          filter(event => event.key === SESSION_EXPIRED_KEY && event.newValue !== null)
        )
        .subscribe(() => this.handleCrossTabLogout());
    });
  }

  private setupSessionCheck(): void {
    timer(this.config.checkIntervalMs, this.config.checkIntervalMs)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.authService.isAuthenticated && !this.warningDialogOpen)
      )
      .subscribe(() => this.checkSessionState());
  }

  private checkSessionState(): void {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    const timeUntilExpiry = this.config.sessionTimeoutMs - timeSinceLastActivity;

    if (timeUntilExpiry <= this.config.warningBeforeExpiryMs) {
      this.showSessionWarning();
    }
  }

  private showSessionWarning(): void {
    if (this.warningDialogOpen) {
      return;
    }

    this.warningDialogOpen = true;

    const dialogRef = this.dialog.open(SessionWarningDialogComponent, {
      disableClose: true
    });
    this.activeWarningDialogRef = dialogRef;

    const timeUntilExpiry = this.config.sessionTimeoutMs - (Date.now() - this.lastActivityTime);
    const autoLogoutDelay = Math.max(timeUntilExpiry, 0);

    this.autoLogoutTimer = setTimeout(() => {
      dialogRef.close(SessionWarningAction.LOGOUT);
    }, autoLogoutDelay);

    dialogRef.afterClosed().subscribe(async (action: SessionWarningAction) => {
      this.clearAutoLogoutTimer();
      this.warningDialogOpen = false;
      this.activeWarningDialogRef = null;

      if (action === SessionWarningAction.EXTEND) {
        await this.extendSession();
      } else if (action === SessionWarningAction.LOGOUT) {
        this.broadcastSessionExpired();
        await this.authService.logout();
      }
    });
  }

  private forceLogoutIfSessionExpired(): void {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;

    if (timeSinceLastActivity >= this.config.sessionTimeoutMs && this.activeWarningDialogRef) {
      this.loggerService.warn('[SessionService] Session expired while tab was in background, forcing logout');
      this.ngZone.run(() => this.activeWarningDialogRef?.close(SessionWarningAction.LOGOUT));
    }
  }

  private dismissWarningIfSessionValid(): void {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    const timeUntilExpiry = this.config.sessionTimeoutMs - timeSinceLastActivity;

    if (timeUntilExpiry > this.config.warningBeforeExpiryMs && this.activeWarningDialogRef) {
      this.ngZone.run(() => this.activeWarningDialogRef?.close());
    }
  }

  private clearSessionExpiredFlag(): void {
    try {
      localStorage.removeItem(SESSION_EXPIRED_KEY);
    } catch (e) {
      this.loggerService.error('localStorage unavailable:', e);
    }
  }

  private broadcastSessionExpired(): void {
    try {
      localStorage.setItem(SESSION_EXPIRED_KEY, Date.now().toString());
    } catch (e) {
      this.loggerService.error('localStorage unavailable:', e);
    }
  }

  private handleCrossTabLogout(): void {
    if (this.activeWarningDialogRef) {
      this.activeWarningDialogRef.close();
    }
    this.ngZone.run(() => this.authService.logout());
  }

  private clearAutoLogoutTimer(): void {
    if (this.autoLogoutTimer) {
      clearTimeout(this.autoLogoutTimer);
      this.autoLogoutTimer = null;
    }
  }

  private async extendSession(): Promise<void> {
    try {
      await firstValueFrom(this.httpClient.get<void>(environment.bffUrl + '/bff/sessions/me'));
      this.recordActivity();
    } catch (error) {
      this.loggerService.warn('[SessionService] Session extension failed, triggering logout', error);
      await this.authService.logout();
    }
  }

  ngOnDestroy(): void {
    this.stopSessionMonitoring();
    this.destroy$.complete();
  }
}
